// Arriving at the free read from a college page (#2608). Founder, 2026-09-30,
// on the NYU page's "See your odds at NYU, free": "clicking this takes to the
// hero landing where there is nothing about NYU... shouldn't we prefill the
// question for the university and ask the student to type the rest or talk
// the rest?"
//
// The college page links /?school=<slug>&from=college_page#see-your-odds.
// The landing page resolves `school` against the college data registry
// (src/data/colleges) and hands the free read an entry: the box names the
// school, and the first read after the picture is that school's. An unknown
// or missing value resolves to null and the free read renders exactly as it
// always has. The registry loads only when the param is present, so the
// generic landing bundle does not grow.
import { useEffect, useState } from 'react';

export const SCHOOL_PARAM = 'school';
export const FROM_PARAM = 'from';
export const COLLEGE_PAGE_FROM = 'college_page';
export const ODDS_ANCHOR = 'see-your-odds';
// The one place a `from` value is accepted from the address bar; anything
// else is dropped, so an event prop can never carry free text from a URL.
const KNOWN_FROM = new Set([COLLEGE_PAGE_FROM]);

// The checked schools that ride into sign-up. Profile's
// TryHeroCollegesPrompt reads this key and offers them to the Launchpad.
export const CARRIED_SCHOOLS_KEY = 'try_hero_schools_anonymous';

/** The college page's "See your odds" link, for any school's page slug. */
export const collegeOddsLink = (slug) =>
    `/?${SCHOOL_PARAM}=${encodeURIComponent(slug)}&${FROM_PARAM}=${COLLEGE_PAGE_FROM}#${ODDS_ANCHOR}`;

/**
 * The entry a search string names, or null. `school` may be the page slug
 * (what the college page sends) or the KB id; a college with no KB id cannot
 * be read, so it resolves to null rather than a box that promises a read it
 * cannot run.
 */
export function schoolEntryFrom(colleges, search) {
    let params;
    try { params = new URLSearchParams(search || ''); } catch { return null; }
    const raw = (params.get(SCHOOL_PARAM) || '').trim();
    if (!raw || raw.length > 100) return null;
    const c = (colleges || []).find((x) => x && (x.slug === raw || x.kbId === raw));
    if (!c || !c.kbId || !c.name) return null;
    const from = params.get(FROM_PARAM);
    return {
        id: c.kbId,
        slug: c.slug,
        name: c.name,
        shortName: c.shortName || c.name,
        branding: c.branding || null,
        testBlind: Boolean(c.testing && c.testing.blind),
        from: KNOWN_FROM.has(from) ? from : null,
    };
}

// The entry for this page load; null until (and unless) it resolves. The
// search string is read once, at mount: the Astra preview token is taken off
// the address bar right after (services/astraLite.js), and that edit must not
// re-resolve the entry.
export function useSchoolEntry() {
    const [search] = useState(() => (typeof window !== 'undefined' ? window.location.search : ''));
    const [entry, setEntry] = useState(null);
    useEffect(() => {
        let wanted = null;
        try { wanted = new URLSearchParams(search).get(SCHOOL_PARAM); } catch { /* no entry */ }
        if (!wanted) return undefined;
        let alive = true;
        import('../data/colleges')
            .then((m) => { if (alive) setEntry(schoolEntryFrom(m.colleges, search)); })
            .catch(() => { /* the registry chunk failed: the generic free read stands */ });
        return () => { alive = false; };
    }, [search]);
    return entry;
}

/** The props an entry adds to the try_hero_* events; nothing without one. */
export function entryProps(entry) {
    if (!entry) return {};
    return entry.from ? { from: entry.from, school_id: entry.id } : { school_id: entry.id };
}

/**
 * The schools to carry into sign-up: every read on screen, plus the entry's
 * school when its read has not landed yet (a visitor who signs in straight
 * away, or whose read was rate-limited, still starts with it offered).
 */
export function carriedSchools(reads, entry) {
    const list = (reads || []).map((r) => ({ id: r.university_id, name: r.name, band: r.band, match: r.match }));
    if (!entry) return list;
    if (!list.some((s) => s.id === entry.id)) list.push({ id: entry.id, name: entry.name });
    return list.map((s) => (s.id === entry.id && entry.from ? { ...s, from: entry.from } : s));
}

export function storeCarriedSchools(reads, entry) {
    const list = carriedSchools(reads, entry);
    if (!list.length) return;
    try { localStorage.setItem(CARRIED_SCHOOLS_KEY, JSON.stringify(list)); } catch { /* private mode */ }
}

/** The event props a carried list implies after sign-up (the prompt's). */
export function carriedEntryProps(schools) {
    const s = (schools || []).find((x) => x && x.from && KNOWN_FROM.has(x.from));
    return s ? { from: s.from, school_id: s.id } : {};
}
